import React, { useState, useEffect } from 'react';
import UploadIcon from '../assets/icons/upload-cloud-01.svg';
import '../styles/ProfilePictureCropper.css';

/**
 * ProfilePictureCropper - A reusable component for uploading and cropping profile pictures
 * 
 * Props:
 * - currentImageUrl: string | null - URL of the current profile picture
 * - onImageChange: (file: File, previewUrl: string) => void - Callback when image is cropped
 * - shape: 'square' | 'circle' - Shape of the crop mask (default: 'square')
 */
function ProfilePictureCropper({ currentImageUrl, onImageChange, shape = 'square' }) {
  const [profilePicturePreview, setProfilePicturePreview] = useState(currentImageUrl);
  const [originalImageData, setOriginalImageData] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const cropMaskSize = 300;

  // Update preview if currentImageUrl changes externally
  useEffect(() => {
    if (currentImageUrl && !profilePicturePreview) {
      setProfilePicturePreview(currentImageUrl);
    }
  }, [currentImageUrl, profilePicturePreview]);

  // Resize image if too large
  const resizeImage = (file, maxWidth = 1200, maxHeight = 1200) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            const resizedFile = new File([blob], file.name, { type: file.type || 'image/jpeg' });
            resolve(resizedFile);
          }, file.type || 'image/jpeg', 0.9);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle profile picture upload
  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const processedFile = await resizeImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          const imageData = reader.result;
          setOriginalImageData(imageData);
          setCropImage(imageData);
          setZoom(1);
          setImagePosition({ x: 0, y: 0 });
          setShowCropModal(true);
        };
        reader.readAsDataURL(processedFile);
      } catch (error) {
        console.error('[ProfilePictureCropper] Error processing image:', error);
      }
    }
  };

  // Handle zoom
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.max(0.5, Math.min(prev + delta, 3)));
  };

  // Handle image drag (mouse)
  const handleImageMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  const handleImageMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    setImagePosition({ x: newX, y: newY });
  };

  const handleImageMouseUp = () => {
    setIsDragging(false);
  };

  // Handle image drag (touch)
  const handleImageTouchStart = (e) => {
    e.stopPropagation();
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - imagePosition.x,
      y: touch.clientY - imagePosition.y
    });
  };

  const handleImageTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    setImagePosition({ x: newX, y: newY });
  };

  const handleImageTouchEnd = () => {
    setIsDragging(false);
  };

  // Apply crop and create preview
  const applyCrop = () => {
    if (!cropImage || !imageNaturalSize.width || !imageNaturalSize.height) return;

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const outputSize = 400;
      
      canvas.width = outputSize;
      canvas.height = outputSize;
      
      const containerSize = 400;
      const containerCenter = containerSize / 2;
      
      const imageAspect = img.width / img.height;
      
      let baseDisplayWidth, baseDisplayHeight;
      if (imageAspect > 1) {
        baseDisplayWidth = containerSize;
        baseDisplayHeight = containerSize / imageAspect;
      } else {
        baseDisplayWidth = containerSize * imageAspect;
        baseDisplayHeight = containerSize;
      }
      
      const currentImageCenterX = containerCenter + imagePosition.x;
      const currentImageCenterY = containerCenter + imagePosition.y;
      
      const offsetX = containerCenter - currentImageCenterX;
      const offsetY = containerCenter - currentImageCenterY;
      
      const pixelsPerContainerPixelX = img.width / (baseDisplayWidth * zoom);
      const pixelsPerContainerPixelY = img.height / (baseDisplayHeight * zoom);
      
      const sourceCenterX = (img.width / 2) + (offsetX * pixelsPerContainerPixelX);
      const sourceCenterY = (img.height / 2) + (offsetY * pixelsPerContainerPixelY);
      
      const avgPixelsPerContainerPixel = (pixelsPerContainerPixelX + pixelsPerContainerPixelY) / 2;
      const sourceCropSize = cropMaskSize * avgPixelsPerContainerPixel;
      const sourceCropRadius = sourceCropSize / 2;
      
      const sourceX = Math.max(0, Math.min(sourceCenterX - sourceCropRadius, img.width - sourceCropSize));
      const sourceY = Math.max(0, Math.min(sourceCenterY - sourceCropRadius, img.height - sourceCropSize));
      const finalSourceSize = Math.min(sourceCropSize, img.width - sourceX, img.height - sourceY);
      
      // Apply circle clip if shape is 'circle'
      if (shape === 'circle') {
        ctx.beginPath();
        ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, 2 * Math.PI);
        ctx.clip();
      }
      
      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        finalSourceSize,
        finalSourceSize,
        0,
        0,
        outputSize,
        outputSize
      );
      
      canvas.toBlob((blob) => {
        const file = new File([blob], 'profile-picture.jpg', { type: 'image/jpeg' });
        const previewUrl = canvas.toDataURL();
        setProfilePicturePreview(previewUrl);
        setShowCropModal(false);
        setCropImage(null);
        setZoom(1);
        setImagePosition({ x: 0, y: 0 });
        
        // Call the parent callback
        if (onImageChange) {
          onImageChange(file, previewUrl);
        }
      }, 'image/jpeg', 0.9);
    };
    img.src = cropImage;
  };

  // Cancel crop
  const cancelCrop = () => {
    setShowCropModal(false);
    setCropImage(null);
    setZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };

  // Handle crop button click - use original image if available
  const handleCropClick = () => {
    const imageToCrop = originalImageData || profilePicturePreview;
    if (imageToCrop) {
      setCropImage(imageToCrop);
      setZoom(1);
      setImagePosition({ x: 0, y: 0 });
      setShowCropModal(true);
    }
  };

  return (
    <>
      <div className="ppc-wrapper">
        {profilePicturePreview ? (
          <div className="ppc-preview-container">
            <div className={`ppc-preview-image-wrapper ${shape === 'circle' ? 'ppc-circle' : ''}`}>
              <img 
                src={profilePicturePreview} 
                alt="Profile" 
                className="ppc-preview-image"
              />
            </div>
            <div className="ppc-preview-actions">
              <button
                type="button"
                className="ppc-crop-button"
                onClick={handleCropClick}
              >
                Crop
              </button>
              <label className="ppc-replace-button">
                Replace
                <input
                  type="file"
                  accept="image/*,.webp,image/webp"
                  onChange={handleProfilePictureChange}
                  className="ppc-upload-input"
                />
              </label>
            </div>
          </div>
        ) : (
          <label className="ppc-upload-label">
            <img src={UploadIcon} alt="Upload" className="ppc-upload-icon" />
            <span>Upload Picture</span>
            <input
              type="file"
              accept="image/*,.webp,image/webp"
              onChange={handleProfilePictureChange}
              className="ppc-upload-input"
            />
          </label>
        )}
      </div>

      {/* Crop Modal */}
      {showCropModal && cropImage && (
        <div className="ppc-crop-modal" onClick={cancelCrop}>
          <div className="ppc-crop-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="ppc-crop-modal-title">Crop Profile Picture</h3>
            <div 
              className="ppc-crop-container"
              onWheel={handleWheel}
            >
              <div 
                className="ppc-crop-image-container"
                onMouseDown={handleImageMouseDown}
                onMouseMove={handleImageMouseMove}
                onMouseUp={handleImageMouseUp}
                onMouseLeave={handleImageMouseUp}
                onTouchStart={handleImageTouchStart}
                onTouchMove={handleImageTouchMove}
                onTouchEnd={handleImageTouchEnd}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              >
                <img 
                  src={cropImage} 
                  alt="Crop" 
                  className="ppc-crop-image"
                  onLoad={(e) => {
                    const img = e.target;
                    setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
                  }}
                  style={{
                    transform: `translate(-50%, -50%) translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${zoom})`,
                    transformOrigin: 'center center'
                  }}
                />
              </div>
              {/* Crop mask overlay */}
              <div className={`ppc-crop-overlay ${shape === 'circle' ? 'ppc-circle' : ''}`} />
            </div>
            {/* Zoom controls */}
            <div className="ppc-crop-zoom-controls">
              <button
                type="button"
                className="ppc-crop-zoom-button"
                onClick={handleZoomOut}
              >
                −
              </button>
              <span className="ppc-crop-zoom-value">{Math.round(zoom * 100)}%</span>
              <button
                type="button"
                className="ppc-crop-zoom-button"
                onClick={handleZoomIn}
              >
                +
              </button>
            </div>
            <div className="ppc-crop-actions">
              <button
                type="button"
                className="ppc-crop-cancel"
                onClick={cancelCrop}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ppc-crop-apply"
                onClick={applyCrop}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProfilePictureCropper;





